/**
 * Row rendering, shared by the home screen and the three library screens.
 *
 * Extracted when the library screens stopped being grids. Before that, rails existed only
 * on the homepage and living inside `Home.tsx` was reasonable; now four screens render the
 * same shape from two different endpoints, and a second copy would be the kind of
 * duplication that drifts silently — a card gains a badge in one place and not the other,
 * and nothing fails.
 *
 * Continue Watching keeps its own card because it carries two things no other row has: a
 * progress bar and, for a series, the episode it stopped on. Those are the entire reason
 * the row is worth showing, so they are not worth generalising away.
 */
import { Children, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { CatalogItemDto, ContinueWatchingDto, DiscoveryRowDto } from '@filmodasi/shared';
import { useScrollReveal } from '../motion';
import { MediaImage } from './MediaImage';
import { ItemCard } from './ui';

function minutes(sec: number): string {
  const m = Math.round(sec / 60);
  if (m < 60) return `${m} dk`;
  return `${Math.floor(m / 60)} sa ${m % 60} dk`;
}

export function ContinueCard({ entry }: { entry: ContinueWatchingDto }) {
  const { item, series, progress, nextEpisode } = entry;
  const art = item.poster ?? item.logo ?? series?.poster ?? series?.logo;
  const remaining =
    progress.durationSec && progress.durationSec > progress.positionSec
      ? progress.durationSec - progress.positionSec
      : null;

  const episodeLabel =
    item.seasonNumber != null && item.episodeNumber != null
      ? `S${String(item.seasonNumber).padStart(2, '0')}B${String(item.episodeNumber).padStart(2, '0')}`
      : null;

  return (
    <Link to={`/watch/${item.id}`} className="card card--poster continue" title={item.title}>
      <div className="card__art">
        <MediaImage src={art} shape="poster" fallback={item.title} seed={item.id} />
        {/* Zero-width for a next episode, which has not been started — the bar is there
            so the row's cards stay the same height, not to claim progress that
            does not exist. */}
        <div className="continue__bar">
          <span style={{ width: `${nextEpisode ? 0 : Math.min(100, progress.percent)}%` }} />
        </div>
        {nextEpisode && <span className="card__badge card__badge--next">Sıradaki</span>}
      </div>
      <span className="card__title">{series?.title ?? item.title}</span>
      <span className="card__meta">
        {episodeLabel ? `${episodeLabel} · ` : ''}
        {nextEpisode ? 'başlamadı' : remaining ? `${minutes(remaining)} kaldı` : 'devam et'}
      </span>
    </Link>
  );
}

export function Rail({
  title,
  action,
  seed,
  children,
}: {
  title: string;
  /** "Tümünü Gör", where a row has a grid behind it. Omitted where it does not. */
  action?: { label: string; onClick: () => void };
  /**
   * The item a `because` row was built from, shown as a thumbnail beside the heading.
   *
   * The server already writes the sentence — "Yıldızlar Savaşı izlediğin için" — so this
   * adds nothing the text does not say. What it adds is recognition: a poster is read at a
   * glance and a title in a heading is read word by word, and the whole purpose of a
   * because-row is to make the connection obvious before anyone reads anything.
   */
  seed?: CatalogItemDto | null;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);

  /*
   * The row settles in as it scrolls into view, and does nothing at all on the television,
   * under reduced motion, or if the animation chunk never arrives — the tween animates
   * *from* hidden, so "no motion" resolves to "already visible" rather than "invisible".
   *
   * Keyed on the child count so a row whose items arrive after the first paint — every
   * row here, since they come from a fetch — is revealed rather than being measured while
   * empty and skipped.
   */
  const count = Children.count(children);
  useScrollReveal(ref, { selector: '.card', key: count });

  return (
    <section className="row" ref={ref}>
      <header className="row__head">
        {seed && (
          // `aria-hidden`: the heading beside it already names the item, and a screen
          // reader announcing the poster as well would read the title twice.
          <span className="row__seed" aria-hidden>
            <MediaImage src={seed.poster ?? seed.logo} shape="poster" seed={seed.id} />
          </span>
        )}
        <h2 className="row__title">{title}</h2>
        {action && (
          <button type="button" className="row__action" onClick={action.onClick}>
            {action.label}
          </button>
        )}
      </header>
      <div className="row__rail">{children}</div>
    </section>
  );
}

/**
 * One discovery row.
 *
 * `continueWatching` is passed separately rather than read from the row: the row carries
 * plain items so that any client can render it, while a client that knows about progress
 * bars uses the richer shape. Matching them by item id keeps the server free to reorder
 * either list without the two falling out of step.
 */
export function DiscoveryRow({
  row,
  continueWatching,
  onSeeAll,
}: {
  row: DiscoveryRowDto;
  continueWatching?: ContinueWatchingDto[];
  onSeeAll?: (seeAll: NonNullable<DiscoveryRowDto['seeAll']>) => void;
}) {
  if (row.kind === 'continue' && continueWatching?.length) {
    return (
      <Rail title={row.title}>
        {continueWatching.map((entry) => (
          <ContinueCard key={entry.item.id} entry={entry} />
        ))}
      </Rail>
    );
  }

  const seeAll = row.seeAll;
  return (
    <Rail
      title={row.title}
      seed={row.seed}
      action={seeAll && onSeeAll ? { label: 'Tümünü Gör', onClick: () => onSeeAll(seeAll) } : undefined}
    >
      {row.items.map((item) => (
        <ItemCard key={item.id} item={item} />
      ))}
    </Rail>
  );
}
